import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  getConciliacionPendientes,
  uploadConciliacionOrdenPago,
} from "./ApiServices";
import { descargarEjemploConciliacionCsv } from "./conciliacionCsvEjemplo";
import { formatCuit, formatMoneda } from "./utils";

const extraerFilas = (data, claves) => {
  for (const clave of claves) {
    if (Array.isArray(data?.[clave]) && data[clave].length > 0) {
      return data[clave];
    }
  }
  return [];
};

const resumenConciliacion = (data) => {
  const r = data?.resumen ?? {};
  return {
    movimientos:
      r.movimientos_csv ??
      r.movimientos ??
      r.total_movimientos ??
      data?.total_movimientos,
    matcheos:
      r.matcheos ??
      r.conciliadas ??
      r.total_matcheos ??
      data?.total_matcheos,
    sinMatch:
      r.sin_match ??
      r.sin_matcheo ??
      r.no_matcheados ??
      data?.total_sin_match,
    aplicadas: r.aplicadas ?? r.conciliadas_aplicadas,
  };
};

const filaImporte = (row) => {
  if (row.importe != null) return Number(row.importe);
  if (row.importe_centavos != null) return Number(row.importe_centavos) / 100;
  if (row.total_neto != null) return Number(row.total_neto);
  return null;
};

const TablaResultados = ({ titulo, filas, vacio }) => {
  if (!filas.length) {
    return vacio ? (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {vacio}
      </Typography>
    ) : null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {titulo}
      </Typography>
      <TableContainer sx={{ maxHeight: 220, border: "1px solid #e0e0e0", borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {["N° OP", "Proveedor", "Importe", "Detalle"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filas.map((row, index) => {
              const importe = filaImporte(row);
              const detalle =
                row.motivo ||
                row.detalle ||
                row.mensaje ||
                row.info_libre ||
                row.observacion ||
                "—";
              return (
                <TableRow key={row.id ?? row.id_orden_pago ?? row.nro_op ?? index}>
                  <TableCell>{row.nro_op ?? row.id_orden_pago ?? "—"}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap title={row.razon_social}>
                      {row.razon_social ?? row.proveedor?.razon_social ?? "—"}
                    </Typography>
                    {(row.nro_cuit || row.proveedor?.nro_cuit) && (
                      <Typography variant="caption" color="text.secondary">
                        {formatCuit(row.nro_cuit || row.proveedor?.nro_cuit)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{importe != null ? formatMoneda(importe) : "—"}</TableCell>
                  <TableCell>
                    <Typography variant="caption" noWrap title={String(detalle)}>
                      {detalle}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default function ConciliacionBancariaDialog({ open, onClose, onConciliacionAplicada }) {
  const inputRef = useRef(null);
  const [archivo, setArchivo] = useState(null);
  const [pendientes, setPendientes] = useState([]);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [errorPendientes, setErrorPendientes] = useState("");
  const [procesando, setProcesando] = useState(false);
  const [modo, setModo] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState("");

  const resetEstado = useCallback(() => {
    setArchivo(null);
    setResultado(null);
    setModo(null);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const cargarPendientes = useCallback(async () => {
    setLoadingPendientes(true);
    setErrorPendientes("");
    try {
      const data = await getConciliacionPendientes();
      const lista =
        data.ordenes_pago ??
        data.pendientes ??
        data.ordenes ??
        (Array.isArray(data) ? data : []);
      setPendientes(Array.isArray(lista) ? lista : []);
    } catch (err) {
      setPendientes([]);
      setErrorPendientes(err.message || "Error al cargar pendientes");
    } finally {
      setLoadingPendientes(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      resetEstado();
      return;
    }
    cargarPendientes();
  }, [open, cargarPendientes, resetEstado]);

  const matcheos = useMemo(
    () =>
      extraerFilas(resultado, [
        "matcheos",
        "matches",
        "conciliadas",
        "conciliaciones",
        "resultados",
      ]),
    [resultado]
  );

  const sinMatch = useMemo(
    () =>
      extraerFilas(resultado, [
        "sin_match",
        "sin_matcheo",
        "no_matcheados",
        "movimientos_sin_match",
      ]),
    [resultado]
  );

  const resumen = useMemo(
    () => (resultado ? resumenConciliacion(resultado) : null),
    [resultado]
  );

  const advertencias = useMemo(() => {
    const adv = resultado?.advertencias ?? resultado?.warnings ?? [];
    return Array.isArray(adv) ? adv : [];
  }, [resultado]);

  const handleArchivo = (e) => {
    const file = e.target.files?.[0] ?? null;
    setArchivo(file);
    setResultado(null);
    setModo(null);
    setError("");
  };

  const ejecutarUpload = async (confirmar) => {
    if (!archivo) {
      setError("Seleccione un archivo CSV de conciliación bancaria");
      return;
    }

    setProcesando(true);
    setError("");

    try {
      const data = await uploadConciliacionOrdenPago(archivo, confirmar);
      setResultado(data);
      setModo(confirmar ? "aplicado" : "preview");

      if (confirmar) {
        onConciliacionAplicada?.(data);
        cargarPendientes();
      }
    } catch (err) {
      setError(err.message || "Error al procesar el archivo");
    } finally {
      setProcesando(false);
    }
  };

  const puedeConfirmar =
    Boolean(archivo) &&
    modo === "preview" &&
    !procesando &&
    (matcheos.length > 0 || Number(resumen?.matcheos) > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Conciliación bancaria</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Importe un extracto <strong>DNCTASCSV_V2</strong>. La vista previa no modifica
          datos; al confirmar se marcan imputaciones, comprobantes y OP como conciliadas.
          Los importes en filas <strong>M</strong> van en centavos; use código de operación{" "}
          <strong>300</strong> y texto <strong>TRFDATPROV</strong> en info libre.
        </Alert>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={descargarEjemploConciliacionCsv}
          >
            Descargar CSV de ejemplo
          </Button>
          <Typography variant="caption" color="text.secondary">
            Datos ficticios — solo referencia de formato
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              OP pendientes de conciliar
            </Typography>
            {loadingPendientes ? (
              <CircularProgress size={18} sx={{ mt: 0.5 }} />
            ) : errorPendientes ? (
              <Typography variant="caption" color="error">
                {errorPendientes}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {pendientes.length} orden{pendientes.length === 1 ? "" : "es"} en estado{" "}
                <Chip label="generada" size="small" variant="outlined" sx={{ mx: 0.5 }} />
              </Typography>
            )}
          </Box>
          <Button size="small" onClick={cargarPendientes} disabled={loadingPendientes}>
            Actualizar
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
            {archivo ? "Cambiar archivo" : "Seleccionar CSV"}
            <input
              ref={inputRef}
              type="file"
              hidden
              accept=".csv,text/csv"
              onChange={handleArchivo}
            />
          </Button>
          {archivo ? (
            <Typography variant="body2" color="text.secondary" noWrap title={archivo.name}>
              {archivo.name}
            </Typography>
          ) : null}
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {modo === "aplicado" ? (
          <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2 }}>
            Conciliación aplicada correctamente.
            {resultado?.msg ? ` ${resultado.msg}` : ""}
          </Alert>
        ) : null}

        {resultado && resumen ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            {resumen.movimientos != null ? (
              <Chip label={`Movimientos CSV: ${resumen.movimientos}`} size="small" />
            ) : null}
            {resumen.matcheos != null ? (
              <Chip
                label={`Matcheos: ${resumen.matcheos}`}
                size="small"
                color="success"
                variant="outlined"
              />
            ) : null}
            {resumen.sinMatch != null ? (
              <Chip
                label={`Sin match: ${resumen.sinMatch}`}
                size="small"
                color="warning"
                variant="outlined"
              />
            ) : null}
            {resumen.aplicadas != null && modo === "aplicado" ? (
              <Chip
                label={`Conciliadas: ${resumen.aplicadas}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            ) : null}
          </Stack>
        ) : null}

        {advertencias.length > 0 ? (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {advertencias.map((msg, i) => (
              <Alert key={i} severity="warning">
                {typeof msg === "string" ? msg : msg?.mensaje || JSON.stringify(msg)}
              </Alert>
            ))}
          </Stack>
        ) : null}

        <TablaResultados
          titulo={modo === "aplicado" ? "OP conciliadas" : "Vista previa — matcheos"}
          filas={matcheos}
          vacio={
            modo === "preview"
              ? "No se encontraron matcheos en la vista previa."
              : null
          }
        />

        <TablaResultados
          titulo="Movimientos / OP sin match"
          filas={sinMatch}
          vacio={modo === "preview" ? "No hay registros sin match." : null}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={procesando}>
          Cerrar
        </Button>
        <Button
          variant="outlined"
          startIcon={
            procesando && modo !== "aplicado" ? (
              <CircularProgress size={16} />
            ) : (
              <VisibilityIcon />
            )
          }
          disabled={!archivo || procesando}
          onClick={() => ejecutarUpload(false)}
        >
          Vista previa
        </Button>
        <Button
          variant="contained"
          startIcon={
            procesando && modo === "aplicado" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CheckCircleOutlineIcon />
            )
          }
          disabled={!puedeConfirmar}
          onClick={() => ejecutarUpload(true)}
        >
          Confirmar conciliación
        </Button>
      </DialogActions>
    </Dialog>
  );
}
